import { useState, useEffect } from 'react';
import { CalendarDays, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { CURRENCY_SYMBOLS } from '@/lib/currencyConversion';
import {
  type RecurringExpense,
  type RecurringFrequency,
  getCategoryIcon,
  getDueDateLabel,
  getDueUrgencyClass,
  getUpcomingExpenses,
} from '@/lib/recurringCalculations';

interface UpcomingPaymentsProps {
  userId: string;
  refreshTrigger?: number;
}

export const UpcomingPayments = ({ userId, refreshTrigger }: UpcomingPaymentsProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [upcomingExpenses, setUpcomingExpenses] = useState<RecurringExpense[]>([]);
  const [currency, setCurrency] = useState('EUR');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;
      
      setIsLoading(true);
      try {
        // Get user's currency
        const { data: settings } = await supabase
          .from('budget_settings')
          .select('currency')
          .eq('user_id', userId)
          .maybeSingle();
        
        if (settings?.currency) {
          setCurrency(settings.currency);
        }

        // Get recurring expenses
        const { data, error } = await supabase
          .from('recurring_expenses')
          .select('*')
          .eq('user_id', userId)
          .eq('is_active', true);

        if (error) throw error;
        
        // Cast the data and get upcoming ones
        const typedData = (data || []).map(item => ({
          ...item,
          frequency: item.frequency as RecurringFrequency,
        }));
        
        const upcoming = getUpcomingExpenses(typedData, 7);
        setUpcomingExpenses(upcoming);
      } catch (error) {
        console.error('Load upcoming payments error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId, refreshTrigger]);

  const currencySymbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency;
  const weekTotal = upcomingExpenses.reduce((sum, e) => sum + e.amount, 0);

  // Don't show if loading or no upcoming expenses
  if (isLoading) {
    return null;
  }

  if (upcomingExpenses.length === 0) {
    return null;
  }

  return (
    <Card className="border-warning/30 bg-warning/5">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-warning" />
            Upcoming This Week
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={() => navigate('/settings')}
          >
            Manage
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        {upcomingExpenses.slice(0, 3).map((expense) => (
          <div
            key={expense.id}
            className="flex items-center justify-between py-1"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm">{getCategoryIcon(expense.category)}</span>
              <div>
                <span className={`text-sm ${getDueUrgencyClass(expense.next_due_date)}`}>
                  {getDueDateLabel(expense.next_due_date)}
                </span>
                <span className="text-sm text-muted-foreground ml-2">
                  {expense.name}
                </span>
              </div>
            </div>
            <span className="font-medium text-sm">
              {currencySymbol}{expense.amount.toFixed(2)}
            </span>
          </div>
        ))}
        
        {upcomingExpenses.length > 3 && (
          <p className="text-xs text-muted-foreground text-center pt-1">
            +{upcomingExpenses.length - 3} more this week
          </p>
        )}

        <div className="pt-2 border-t mt-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Week total</span>
            <span className="font-semibold text-warning">
              {currencySymbol}{weekTotal.toFixed(2)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
